import * as React from 'react';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { useIdCounter } from '@rbilabs/components-library/build/hooks/use-id-counter';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import IconCheck from 'components/icons/check';
import { useLocale } from 'state/intl';
import { PROD_DEFAULT_REGION } from 'state/intl/supported-regions';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isMobile } from 'utils/environment';
import { FormValidationState } from 'utils/form';
import { format, parsePhoneNumberFromString } from 'utils/libphonenumber';

import { COUNTRY_CODES, PHONE_NUMBER_FORMAT_STANDARDS } from './constants';
import {
  ArrowDownContainer,
  CountryCode,
  FlagEmoji,
  FloatingLabelInputWrapper,
  HiddenSelect,
  Label,
  Message,
  NationalNumberWrapper,
  PhoneInput,
  PhoneInputContainer,
  SelectWrapper,
  UnverifiedBadge,
  VerificationBadgeContainer,
  VerificationIconContainer,
} from './styled';
import {
  IFloatingLabelPhoneInputProps,
  IFormikFloatingLabelPhoneInput,
  IGetValidationState,
} from './types';
import useInputHandles from './use-input-handles';
import useTestIds from './use-test-ids';

const StyledLabel = styled(Label)`
  pointer-events: none;
`;

/**
 * Finds the country option from the country abbreviation
 * @param countryAbrev CountryCode Pass in country code to find the value
 * @returns countryOption from COUNTRY_CODES or undefined
 */
const getCountryOptionFromAbrev = (countryAbrev: string) => {
  return COUNTRY_CODES.find(countryOption => countryOption.value === countryAbrev);
};

/**
 * Finds the country option from the country calling code
 * @param countryCode CountryCode
 * @returns countryOption from COUNTRY_CODES or undefined
 */
const getCountryOptionFromCallingCode = (countryCode: string) => {
  return COUNTRY_CODES.find(countryOption => countryOption.countryCode === countryCode);
};

/**
 * FloatingLabelPhoneInput
 *
 * A component used to enter phone numbers.
 * Comes equipped with automatic formatting.
 */
export const FloatingLabelPhoneInput: FC<IFloatingLabelPhoneInputProps> = ({
  'data-testid': testId,
  autoComplete,
  className,
  footer,
  hasFooter,
  label,
  labelId,
  maxLength,
  message,
  onBlur,
  onChange,
  onFocus,
  readOnly = false,
  verified,
  validation = FormValidationState.PRISTINE,
  value,
  hideCountrySelector,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const inputId = useIdCounter('floating-label-input');
  const messageId = useIdCounter('message');
  const { countryCodeTestId, dropdownTestId, labelTestId, messageTestId, wrapperTestId } =
    useTestIds(testId);
  const enableNumericPhoneValidation = useFlag(LaunchDarklyFlag.ENABLE_NUMERIC_PHONE_VALIDATION);
  const initialPhoneValue = enableNumericPhoneValidation ? value : '';
  /* Ref used to keep country code from resetting while user types */
  const currentValue = useRef(initialPhoneValue);
  const { region } = useLocale();
  const currentOrDefaultRegion = region
    ? region.toString()
    : (PROD_DEFAULT_REGION as unknown as string);
  const [country, setCountry] = useState(currentOrDefaultRegion);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneValue);
  const [inputValue, setInputValue] = useState(value);
  // show verified/unverified badge if there is an initial value
  const [showVerifiedBadge, setShowVerifiedBadge] = useState(!!value);

  const { countryCode, emoji } = getCountryOptionFromAbrev(country) || {};

  const handleChange = async ({ newCountry = country, newPhoneNumber = phoneNumber }) => {
    // hide badge if the value has been touched
    setShowVerifiedBadge(false);

    const selectedCountryOption = getCountryOptionFromAbrev(newCountry);
    const newCountryCode = selectedCountryOption?.countryCode || '';
    const e164FormattedNumber = await format(
      newCountryCode + newPhoneNumber,
      PHONE_NUMBER_FORMAT_STANDARDS.E164
    );

    const newValue = enableNumericPhoneValidation
      ? newPhoneNumber
      : e164FormattedNumber || newCountryCode + newPhoneNumber;
    currentValue.current = newValue;
    onChange(newValue);
  };

  const handleCountryCodeChange = (ev: ChangeEvent<HTMLSelectElement>) => {
    ev.persist();
    setCountry(ev.target.value);

    handleChange({ newCountry: ev.target.value });
  };

  const handlePhoneNumberChange = (ev: ChangeEvent<HTMLInputElement>) => {
    ev.persist();
    setPhoneNumber(ev.target.value);

    handleChange({ newPhoneNumber: ev.target.value });
  };

  const {
    handleBlur,
    handleChange: handleInputChange,
    handleFocus,
  } = useInputHandles({
    inputId,
    isMobile: isMobile(),
    onBlur,
    onChange: handlePhoneNumberChange,
    onFocus,
  });

  /**
   * Parse the phone number if the component receives a new value externally
   * rather than being updated internally
   * ex: async calls
   */
  useEffect(() => {
    (async () => {
      if (value !== currentValue.current) {
        if (enableNumericPhoneValidation) {
          setPhoneNumber(currentValue.current);
        } else {
          setShowVerifiedBadge(true);

          const {
            country: parsedCountry = '',
            countryCallingCode = '',
            nationalNumber = '',
          } = (await parsePhoneNumberFromString(value)) || {};

          /* libphonenumber-js has countryCallingCode and nationalNumber typed as enums
          that extend String */
          if (parsedCountry) {
            setCountry(parsedCountry);
          } else {
            /* sometimes the parsing won't return a country abbreviation but will return a
          countryCallingCode. In that case, look up the calling code in our options or set it to the default. */
            const countryOption = getCountryOptionFromCallingCode(`+${countryCallingCode}`);
            setCountry(countryOption?.value || currentOrDefaultRegion);
          }
          setPhoneNumber(nationalNumber as string);
        }
      }
    })();
  }, [currentOrDefaultRegion, value, enableNumericPhoneValidation]);

  const handlePhoneInputChange = useCallback(
    e => {
      // Only allow user to enter numbers
      const numberRegex = enableNumericPhoneValidation ? /[^+0-9]/ : /\D/g;
      e.target.value = e.target.value.replace(numberRegex, '');
      handleInputChange(e);
    },
    [handleInputChange, enableNumericPhoneValidation]
  );

  useEffect(() => {
    if (enableNumericPhoneValidation) {
      if (inputValue !== phoneNumber) {
        return setInputValue(phoneNumber);
      }
    }

    format(countryCode + phoneNumber, PHONE_NUMBER_FORMAT_STANDARDS.NATIONAL).then(
      formattedValue => {
        const resolvedValue = formattedValue || phoneNumber;
        // If formatting fails don't update field
        if (!resolvedValue) {
          if (inputValue.length > 1) {
            return;
          }
          // When we delete the last digit we want to make sure we set the input as empty
          return setInputValue('');
        }
        if (inputValue !== resolvedValue) {
          setInputValue(resolvedValue);
        }
      }
    );
  }, [countryCode, inputValue, phoneNumber, value, enableNumericPhoneValidation]);

  const inputProps = useMemo(
    () => ({
      'aria-describedby': messageId,
      'aria-labelledby': labelId,
      autoComplete,
      'data-testid': testId,
      hasFooter: !!hasFooter,
      id: rest.id || inputId,
      maxLength,
      onBlur: handleBlur,
      onChange: handlePhoneInputChange,
      onFocus: handleFocus,
      readOnly,
      disabled: readOnly,
      type: 'text',
    }),
    [
      messageId,
      labelId,
      autoComplete,
      testId,
      hasFooter,
      rest.id,
      inputId,
      maxLength,
      handleBlur,
      handlePhoneInputChange,
      handleFocus,
      readOnly,
    ]
  );

  // only show verification badge if `verified` is a boolean
  let validatedBadge = null;
  if (typeof verified === 'boolean') {
    validatedBadge = verified ? (
      <VerificationIconContainer validation={FormValidationState.VALID}>
        <IconCheck data-testid="verification-badge" />
      </VerificationIconContainer>
    ) : (
      <>
        <VerificationIconContainer>
          <IconCheck data-testid="verification-badge" />
        </VerificationIconContainer>
        <VerificationBadgeContainer>
          <UnverifiedBadge>{formatMessage({ id: 'unverified' })}</UnverifiedBadge>
        </VerificationBadgeContainer>
      </>
    );
  }
  return (
    <FloatingLabelInputWrapper className={className} data-testid={wrapperTestId}>
      <PhoneInputContainer readOnly={readOnly}>
        {!hideCountrySelector && (
          <SelectWrapper validation={validation}>
            <HiddenSelect
              data-private
              data-testid={dropdownTestId}
              disabled={readOnly}
              name={rest.name}
              onChange={handleCountryCodeChange}
              value={country}
            >
              {COUNTRY_CODES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.emoji}&nbsp;&nbsp;{option.country} {option.value}
                </option>
              ))}
            </HiddenSelect>
            <FlagEmoji aria-hidden>{emoji}</FlagEmoji>
            <ArrowDownContainer aria-hidden>
              <Icon icon="select" color="icon-form-input-action" width="24px" aria-hidden />
            </ArrowDownContainer>
          </SelectWrapper>
        )}
        <NationalNumberWrapper validation={validation} hideCountrySelector={hideCountrySelector}>
          <PhoneInput
            maxLength={10}
            {...inputProps}
            {...rest}
            value={inputValue}
            type="tel"
            data-private
            hideCountrySelector={hideCountrySelector}
          />
          {!hideCountrySelector && (
            <CountryCode data-testid={countryCodeTestId}>{countryCode}</CountryCode>
          )}
        </NationalNumberWrapper>

        {hasFooter && footer}
        <StyledLabel id={labelId} htmlFor={rest.id || inputId} data-testid={labelTestId} hasValue>
          {label}
        </StyledLabel>

        {showVerifiedBadge && validatedBadge}
      </PhoneInputContainer>
      {message && (
        <Message id={messageId} data-testid={messageTestId} validation={validation}>
          {message}
        </Message>
      )}
    </FloatingLabelInputWrapper>
  );
};

const getValidationState = ({ touched = false, error }: IGetValidationState) => {
  if (!touched) {
    return FormValidationState.PRISTINE;
  }

  return error ? FormValidationState.INVALID : FormValidationState.VALID;
};

export const FormikFloatingLabelPhoneInput: FC<IFormikFloatingLabelPhoneInput> = ({
  field,
  form,
  ...props
}) => {
  // TODO: remove when phone numbers are stored with a leading `+`
  const formattedValue = field.value.startsWith('+') ? field.value : `+${field.value}`;

  const touched = form.touched[field.name];
  let error = form.errors[field.name];

  if (Array.isArray(error)) {
    error = error?.[0];
  }

  const handleChange = (phoneNumber: string) => {
    form.setFieldValue(field.name, phoneNumber);
  };

  return (
    <FloatingLabelPhoneInput
      {...field}
      {...props}
      value={formattedValue}
      message={touched ? error : ''}
      onChange={handleChange}
      validation={getValidationState({ touched, error })}
    />
  );
};
